import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { StandardButton, FIELD_CLASS } from '../../../../../component/common/StandardUI';

const INPUT_CLASS = FIELD_CLASS;

const AddKeyDateModal = ({ open, monthLabel, onClose, onSubmit, submitting }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [day, setDay] = useState('');
  const [wholeMonth, setWholeMonth] = useState(false);
  const [generateAiSuggestion, setGenerateAiSuggestion] = useState(true);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDay('');
    setWholeMonth(false);
    setGenerateAiSuggestion(true);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!wholeMonth && !day) {
      setError('Select a day, or mark this as a whole-month event');
      return;
    }

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      day: wholeMonth ? null : Number(day),
      generateAiSuggestion
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') handleClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      <div
        className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-input shadow-xl w-full max-w-md mx-4 p-6"
        onKeyDown={handleKeyDown}
      >
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
            Add custom key date
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">Adding to {monthLabel}</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="keyDateTitle"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Title <span className="text-red-500">*</span>
            </label>
            <input
              ref={inputRef}
              type="text"
              id="keyDateTitle"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError('');
              }}
              placeholder="e.g. Reuben Hale's Birthday"
              className={INPUT_CLASS}
              maxLength={100}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="keyDateDay"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Day of the month
            </label>
            <input
              type="number"
              id="keyDateDay"
              min={1}
              max={31}
              value={day}
              disabled={wholeMonth}
              onChange={(e) => {
                setDay(e.target.value);
                setError('');
              }}
              placeholder="e.g. 2"
              className={`${INPUT_CLASS} disabled:opacity-50`}
            />
            <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={wholeMonth}
                onChange={(e) => {
                  setWholeMonth(e.target.checked);
                  setError('');
                }}
                className="rounded border-gray-300 text-shoorah-primary focus:ring-shoorah-primary"
              />
              This runs for the whole month
            </label>
          </div>

          <div className="mb-4">
            <label
              htmlFor="keyDateDescription"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Description
            </label>
            <textarea
              id="keyDateDescription"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this date about?"
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          <label className="flex items-center gap-2 mb-4 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={generateAiSuggestion}
              onChange={(e) => setGenerateAiSuggestion(e.target.checked)}
              className="rounded border-gray-300 text-shoorah-primary focus:ring-shoorah-primary"
            />
            Generate an AI suggestion for this date
          </label>

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          <div className="flex gap-3">
            <StandardButton
              text="Cancel"
              variant="secondary"
              onClick={handleClose}
              className="flex-1"
            />
            <StandardButton
              text={submitting ? 'Saving...' : 'Add key date'}
              type="submit"
              disabled={!title.trim() || submitting}
              className="flex-1"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

AddKeyDateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  monthLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool
};

export default AddKeyDateModal;
